import React, {
	memo,
	RefObject,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import createJustifiedLayout from "justified-layout";

import { useLabels } from "../../../hooks/useLabels";
import { useResizeObserver } from "../../../hooks/useResizeObserver";
import { useSelectedZone } from "../../../hooks/useSelection";
import { useTextZoneDashboard } from "../../../hooks/useTextZoneDashboard";
import { useUsedTextForegroundColors } from "../../../hooks/useUsedTextForegroundColors";
import { isTextColorPanelSupported } from "../../bottom-text-menus/text-color-panel/menu-info";
import { useSelectedTextColor } from "../../bottom-text-menus/text-color-panel/use-selected-text-color";
import { useTextColors } from "../../bottom-text-menus/text-color-panel/use-text-colors";
import { ColorDot } from "../../color-dot";
import { ISidebarContentRef, SidebarPanel } from "../sidebar-panel";
import { labels } from "./labels";

import "./sidebar-text-color-panel.css";

type JustifyLayoutOptions = Pick<
	NonNullable<Parameters<typeof createJustifiedLayout>[1]>,
	"containerPadding" | "targetRowHeight" | "boxSpacing" | "targetRowHeightTolerance" // TODO: Add more when needed
>;

/**
 * Hook around logic for `createJustifiedLayout`
 * Currently created for square aspect ratio
 */
function useJustifyLayout(
	ref: RefObject<ISidebarContentRef | null>,
	itemCount: number,
	options: JustifyLayoutOptions,
) {
	const { containerPadding, targetRowHeight, boxSpacing, targetRowHeightTolerance } = options; // TODO: extract more when needed
	const [containerWidth, setContainerWidth] = useState(0);

	const getContainerWidth = useCallback(() => {
		const panel = ref.current?.el;
		if (!panel) return 0;

		// Measure inner width of the panel
		const style = getComputedStyle(panel);
		const paddingLeft = parseFloat(style.paddingLeft) || 0;
		const paddingRight = parseFloat(style.paddingRight) || 0;
		const innerWidth = panel.clientWidth - paddingLeft - paddingRight;
		return innerWidth;
	}, [ref]);

	useLayoutEffect(() => {
		if (ref.current?.el) {
			setContainerWidth(getContainerWidth());
		}
	}, [getContainerWidth, ref]);

	useResizeObserver({ current: ref.current?.el }, () => {
		setContainerWidth(getContainerWidth());
	});

	const { boxes, containerHeight } = useMemo(() => {
		if (!itemCount || !containerWidth) {
			return { boxes: null, containerHeight: 0 };
		}

		const justifyLayoutOptions: NonNullable<Parameters<typeof createJustifiedLayout>[1]> = {
			containerPadding,
			targetRowHeight,
			boxSpacing,

			containerWidth,
			forceAspectRatio: 1,
		};

		// Passing undefined gives different behavior, so only pass if defined
		if (targetRowHeightTolerance !== undefined) {
			justifyLayoutOptions.targetRowHeightTolerance = targetRowHeightTolerance;
		}

		return createJustifiedLayout(
			Array.from({ length: itemCount }, () => 1),
			justifyLayoutOptions,
		);
	}, [
		boxSpacing,
		containerPadding,
		containerWidth,
		targetRowHeightTolerance,
		itemCount,
		targetRowHeight,
	]);

	return {
		containerWidth,
		containerHeight,
		boxes,
	};
}

const colorDotMargin = 16;

export const SidebarTextColorPanel = memo(function SidebarTextColorPanel() {
	const selectedZone = useSelectedZone();
	const textZoneDashboard = useTextZoneDashboard();
	const usedColors = useUsedTextForegroundColors({
		fromConfiguration: true,
		fromDefinition: true,
	});
	const panelColors = useTextColors();
	const selectedTextColor = useSelectedTextColor(selectedZone);
	const showTextColors = isTextColorPanelSupported(selectedZone, textZoneDashboard);
	const [[titleLabel, recommendedColorsLabel, allColorsLabel], areLabelsLoading] = useLabels([
		labels.title,
		labels.recommendedColors,
		labels.allColors,
	]);

	const onSelectColor = useCallback(
		(clickedColor: eXpress.core.IColor) => {
			if (!textZoneDashboard.canSetTextColor(selectedZone)) return;

			const newColor = new eXpress.core.Color(clickedColor.hexColorString);
			textZoneDashboard.setTextColor(selectedZone, newColor);
		},
		[textZoneDashboard, selectedZone],
	);

	const panelRef = useRef<ISidebarContentRef | null>(null);

	const { boxes: allColorsBoxes, containerHeight: allColorsContainerHeight } = useJustifyLayout(
		panelRef,
		panelColors.length,
		{
			containerPadding: 4,
			targetRowHeight: 32,
			boxSpacing: colorDotMargin,
		},
	);

	// Remark: for small panel sizes, the dots can have a different size as for allColors
	const { boxes: recommendedColorsBoxes, containerHeight: recommendedColorsContainerHeight } =
		useJustifyLayout(panelRef, usedColors.length, {
			containerPadding: 4,
			targetRowHeight: allColorsBoxes?.[0]?.height || 32, // Try to use the same height as the all colors for consistency, or fallback to 32 if not available
			boxSpacing: colorDotMargin,
			targetRowHeightTolerance: 0.4, // Was needed to have the same size
		});

	if (!showTextColors) return null;
	if (areLabelsLoading) return null;

	return (
		<SidebarPanel.Root>
			<SidebarPanel.Header>
				<SidebarPanel.Title>{titleLabel}</SidebarPanel.Title>
			</SidebarPanel.Header>
			<SidebarPanel.Content scrollable fade ref={panelRef}>
				<SidebarPanel.SubTitle>{recommendedColorsLabel}</SidebarPanel.SubTitle>
				<div
					className="sidebar-text-color-panel__colors"
					style={{ height: recommendedColorsContainerHeight }}
				>
					{recommendedColorsBoxes?.map((box, index) => {
						const color = usedColors[index];
						return (
							<ColorDot
								key={`used-${color.hexColorString}`}
								color={color.hexColorString}
								isActive={selectedTextColor === color.hexColorString}
								colorStyle={color.colorStyle}
								onClick={() => onSelectColor(color)}
								style={{
									position: "absolute",
									top: box?.top,
									left: box?.left,
									width: box?.width,
									height: box?.height,
								}}
							/>
						);
					})}
				</div>

				<SidebarPanel.SubTitle>{allColorsLabel}</SidebarPanel.SubTitle>
				<div
					className="sidebar-text-color-panel__colors"
					style={{ height: allColorsContainerHeight }}
				>
					{allColorsBoxes?.map((box, index) => {
						const color = panelColors[index];
						return (
							<ColorDot
								key={color.hexColorString}
								color={color.hexColorString}
								isActive={selectedTextColor === color.hexColorString}
								colorStyle={color.colorStyle}
								onClick={() => onSelectColor(color)}
								style={{
									position: "absolute",
									top: box?.top,
									left: box?.left,
									width: box?.width,
									height: box?.height,
								}}
							/>
						);
					})}
				</div>
			</SidebarPanel.Content>
		</SidebarPanel.Root>
	);
});
